import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROPERTY_TYPES = ["Appartement", "Maison", "Loft", "Studio"];
const WORKS = [
  { v: "renovation_complete", l: "Rénovation complète" },
  { v: "cuisine", l: "Cuisine" },
  { v: "sdb", l: "Salle de bain" },
  { v: "peinture", l: "Peinture & déco" },
  { v: "electricite", l: "Électricité" },
  { v: "plomberie", l: "Plomberie" },
  { v: "sol", l: "Sols & parquet" },
];
const BUDGETS = ["20–40 k€", "40–80 k€", "80–150 k€", "+ 150 k€"];
const TIMELINES = [
  { v: "asap", l: "Dès que possible" },
  { v: "3m", l: "Sous 3 mois" },
  { v: "6m", l: "Sous 6 mois" },
  { v: "flex", l: "Flexible" },
];

export default function QuoteForm() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    property_type: "Appartement",
    surface_m2: 80,
    rooms: 3,
    works: [],
    budget_range: BUDGETS[1],
    timeline: "3m",
    full_name: "",
    email: "",
    phone: "",
    zip_code: "",
    city: "Paris",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleWork = (v) =>
    setForm((f) => ({ ...f, works: f.works.includes(v) ? f.works.filter((x) => x !== v) : [...f.works, v] }));

  const submit = async () => {
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Merci de remplir nom, email et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, surface_m2: Number(form.surface_m2), rooms: Number(form.rooms) });
      setDone(true);
      toast.success("Demande envoyée. Nous vous recontactons sous 48h.");
    } catch (e) {
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const next = () => setStep((s) => Math.min(2, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <section id="devis" data-testid="quote-section" className="section bg-bone">
      <div className="container-max">
        <div className="grid grid-cols-12 gap-12">
          <div className="col-span-12 md:col-span-5">
            <p className="overline">— Devis offert</p>
            <h2 className="font-display font-light text-5xl md:text-6xl leading-[0.95] tracking-tighter mt-6">
              Décrivez<br />
              votre <em className="italic text-terracotta">projet</em>.<br />
              Nous y répondons sous <span className="underline decoration-terracotta decoration-4 underline-offset-8">48 heures</span>.
            </h2>
            <p className="mt-8 text-ink/70 leading-relaxed max-w-md">
              Réponse personnalisée par un chef de projet. Visite technique gratuite sur Paris &amp; Île-de-France. Aucun engagement.
            </p>

            <ul className="mt-10 space-y-3 text-ink/80">
              {["Visite technique gratuite", "Avant-projet sous 10 jours", "Devis détaillé poste par poste", "Aucune sous-traitance cachée"].map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-terracotta" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-12 md:col-span-7 bg-ink text-bone p-8 md:p-12 relative grain">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="min-h-[420px] flex flex-col items-start justify-center"
                  data-testid="quote-success"
                >
                  <div className="w-12 h-12 bg-terracotta flex items-center justify-center mb-6">
                    <Check className="w-6 h-6 text-bone" />
                  </div>
                  <h3 className="font-display text-4xl md:text-5xl leading-tight">Demande reçue.</h3>
                  <p className="mt-4 text-bone/70 max-w-md">
                    Un membre de notre atelier vous contacte sous 48 heures pour fixer la visite technique gratuite.
                  </p>
                </motion.div>
              ) : (
                <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
                  <div className="flex items-center justify-between mb-8">
                    <p className="overline text-bone/60">Étape {step + 1} / 3</p>
                    <div className="flex gap-2">
                      {[0, 1, 2].map((i) => (
                        <span key={i} className={`h-px w-10 ${i <= step ? "bg-terracotta" : "bg-bone/30"}`} />
                      ))}
                    </div>
                  </div>

                  {step === 0 && (
                    <div className="space-y-8">
                      <div>
                        <label className="overline text-bone/60">Type de bien</label>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          {PROPERTY_TYPES.map((t) => (
                            <button
                              key={t}
                              type="button"
                              data-testid={`quote-property-${t}`}
                              onClick={() => update("property_type", t)}
                              className={`border p-4 text-left transition ${form.property_type === t ? "border-terracotta bg-terracotta/10" : "border-bone/30 hover:border-bone"}`}
                            >
                              <span className="font-display text-xl">{t}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="overline text-bone/60">Surface (m²)</label>
                          <input
                            data-testid="quote-surface"
                            type="number"
                            min="10"
                            value={form.surface_m2}
                            onChange={(e) => update("surface_m2", e.target.value)}
                            className="input-sharp text-bone border-bone/40"
                            style={{ borderColor: "#FAFAF8" }}
                          />
                        </div>
                        <div>
                          <label className="overline text-bone/60">Pièces</label>
                          <input
                            data-testid="quote-rooms"
                            type="number"
                            min="1"
                            value={form.rooms}
                            onChange={(e) => update("rooms", e.target.value)}
                            className="input-sharp text-bone"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-8">
                      <div>
                        <label className="overline text-bone/60">Travaux envisagés (plusieurs choix)</label>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {WORKS.map((w) => (
                            <button
                              key={w.v}
                              type="button"
                              data-testid={`quote-work-${w.v}`}
                              onClick={() => toggleWork(w.v)}
                              className={`px-4 py-2 border text-sm transition ${form.works.includes(w.v) ? "border-terracotta bg-terracotta text-bone" : "border-bone/30 hover:border-bone"}`}
                            >
                              {w.l}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="overline text-bone/60">Budget estimé</label>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          {BUDGETS.map((b) => (
                            <button
                              key={b}
                              type="button"
                              data-testid={`quote-budget-${b}`}
                              onClick={() => update("budget_range", b)}
                              className={`border p-3 text-left transition ${form.budget_range === b ? "border-terracotta bg-terracotta/10" : "border-bone/30 hover:border-bone"}`}
                            >
                              {b}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="overline text-bone/60">Démarrage</label>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {TIMELINES.map((t) => (
                            <button
                              key={t.v}
                              type="button"
                              data-testid={`quote-timeline-${t.v}`}
                              onClick={() => update("timeline", t.v)}
                              className={`px-4 py-2 border text-sm transition ${form.timeline === t.v ? "border-terracotta bg-terracotta text-bone" : "border-bone/30 hover:border-bone"}`}
                            >
                              {t.l}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="overline text-bone/60">Nom complet*</label>
                          <input
                            data-testid="quote-name"
                            type="text"
                            required
                            value={form.full_name}
                            onChange={(e) => update("full_name", e.target.value)}
                            className="input-sharp text-bone"
                          />
                        </div>
                        <div>
                          <label className="overline text-bone/60">Email*</label>
                          <input
                            data-testid="quote-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            className="input-sharp text-bone"
                          />
                        </div>
                        <div>
                          <label className="overline text-bone/60">Téléphone*</label>
                          <input
                            data-testid="quote-phone"
                            type="tel"
                            required
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            className="input-sharp text-bone"
                          />
                        </div>
                        <div>
                          <label className="overline text-bone/60">Code postal</label>
                          <input
                            data-testid="quote-zip"
                            type="text"
                            value={form.zip_code}
                            onChange={(e) => update("zip_code", e.target.value)}
                            className="input-sharp text-bone"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="overline text-bone/60">Quelques mots sur le projet</label>
                        <textarea
                          data-testid="quote-message"
                          rows={3}
                          value={form.message}
                          onChange={(e) => update("message", e.target.value)}
                          className="input-sharp text-bone resize-none"
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-10 flex items-center justify-between">
                    <button
                      data-testid="quote-prev"
                      type="button"
                      onClick={prev}
                      disabled={step === 0}
                      className="overline text-bone/70 hover:text-bone disabled:opacity-30"
                    >
                      ← Précédent
                    </button>

                    {step < 2 ? (
                      <button data-testid="quote-next" type="button" onClick={next} className="btn-sharp bg-terracotta border-terracotta">
                        Suivant <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button data-testid="quote-submit" type="button" disabled={loading} onClick={submit} className="btn-sharp bg-terracotta border-terracotta">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer ma demande"}
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
